// ChatbotPopup.js
import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef } from 'react';
import ChatbotService from './ChatbotService';
import ButtonGenerationUtility from './ButtonGenerationUtility';
import '../styles/chatbot.css';
import { useDarkMode } from '../../contexts/DarkModeContext';

const ChatbotPopup = forwardRef(({
  isOpen,
  onClose = () => {},
  formData,
  onDataUpdate,
  onProposeUpdate,
  triggerErrorPopup,
  darkMode,
  onMessageSent,
  user,
  navigate,
  credits,
  isGated,
  onShowCreditGate,
}, ref) => {
  // Initialize messages with the bot's welcome message at the bottom
  const [messages, setMessages] = useState([
    { text: "How can I help you?", sender: "bot", id: Date.now() },
  ]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const [loadingState, setLoadingState] = useState({});

  const [lastBotMessageId, setLastBotMessageId] = useState(null);

  // Auto-focus input when chatbot opens or messages update
  useLayoutEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, isOpen]);

  // Scroll to the latest message when messages update or when chatbot opens
  useEffect(() => {
    if (isOpen) {
      scrollToLatestMessage();
    }
  }, [messages, isOpen]);

  // Handle replaying a pending prompt saved before redirecting to login on desktop
  useEffect(() => {
    if (isOpen && user && credits) {
      const pendingStr = sessionStorage.getItem('coach_pending');
      if (pendingStr) {
        try {
          const pending = JSON.parse(pendingStr);
          const age = Date.now() - pending.timestamp;
          if (age < 600000 && pending.returnTab === 'coach') {
            sessionStorage.removeItem('coach_pending');
            if (isGated && (credits?.remainingCredits || 0) <= 0) {
              setMessages(prev => [...prev, { text: pending.prompt, sender: "user", id: Date.now() }]);
              onShowCreditGate('ai_insufficient');
            } else {
              const runPendingPrompt = async () => {
                setIsProcessing(true);
                // Show user message and typing indicator
                setMessages((prevMessages) => [
                  ...prevMessages,
                  { text: pending.prompt, sender: 'user', id: Date.now() },
                  { type: 'typing', sender: 'bot', id: 'typing-indicator' },
                ]);
                try {
                  const botResponse = await ChatbotService.generateResponse(pending.prompt, formData);
                  let buttons = [];
                  try {
                    buttons = await ButtonGenerationUtility.generateButtons(botResponse);
                  } catch (buttonError) {
                    console.error('Error generating buttons:', buttonError);
                  }
                  const newBotMessage = {
                    ...botResponse,
                    sender: "bot",
                    buttons,
                    id: Date.now(),
                  };
                  setMessages((prevMessages) =>
                    prevMessages
                      .filter((msg) => msg.id !== 'typing-indicator')
                      .concat(newBotMessage)
                  );
                  setLastBotMessageId(newBotMessage.id);
                  if (onMessageSent) {
                    onMessageSent();
                  }
                } catch (error) {
                  console.error("Error replaying pending prompt:", error);
                  setMessages((prevMessages) =>
                    prevMessages
                      .filter((msg) => msg.id !== 'typing-indicator')
                      .concat({
                        text: "Sorry, I couldn't process your request. Please try again later.",
                        sender: "bot",
                        id: Date.now(),
                      })
                  );
                } finally {
                  setIsProcessing(false);
                }
              };
              runPendingPrompt();
            }
          } else {
            sessionStorage.removeItem('coach_pending');
          }
        } catch (e) {
          console.error("Error processing pending prompt", e);
          sessionStorage.removeItem('coach_pending');
        }
      }
    }
  }, [isOpen, user, credits, isGated, formData, onShowCreditGate, onMessageSent]);

  // Remove lastBotMessageId after animation duration
  useEffect(() => {
    if (lastBotMessageId) {
      const timer = setTimeout(() => {
        setLastBotMessageId(null);
      }, 2000); // Duration of the pulse animation

      return () => clearTimeout(timer);
    }
  }, [lastBotMessageId]);

  const scrollToLatestMessage = () => {
    const lastUserMessage = [...messages].reverse().find((msg) => msg.sender === 'user');
    if (lastUserMessage && messagesContainerRef.current) {
      const messageElement = document.getElementById(`message-${lastUserMessage.id}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (!isOpen) return null;

  const handleSendMessage = async () => {
    if (input.trim() === '') return;

    const currentInput = input;

    if (!user) {
      sessionStorage.setItem('coach_pending', JSON.stringify({
        prompt: currentInput,
        returnTab: 'coach',
        timestamp: Date.now()
      }));
      const newUserMessage = { text: currentInput, sender: 'user', id: Date.now() };
      setMessages((prevMessages) => [...prevMessages, newUserMessage]);
      setInput('');
      setIsProcessing(true);
      setLastBotMessageId(null);

      setTimeout(() => {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: "Please sign in or register to use the AI Coach. I'll remember your question and run it as soon as you sign in!",
            sender: "bot",
            id: Date.now()
          }
        ]);
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      }, 500);
      return;
    }

    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      onShowCreditGate('ai_insufficient');
      return;
    }

    const newUserMessage = { text: currentInput, sender: 'user', id: Date.now() };
    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    setInput('');
    setIsProcessing(true);
    setLastBotMessageId(null);

    try {
      // Show typing indicator
      setMessages((prevMessages) => [
        ...prevMessages,
        { type: 'typing', sender: 'bot', id: 'typing-indicator' },
      ]);

      console.log('ChatbotPopup - Sending message in', darkMode ? 'dark' : 'light', 'mode');
      const botResponse = await ChatbotService.generateResponse(currentInput, formData);
      console.log('ChatbotPopup - Received response:', botResponse);

      let buttons = [];
      try {
        buttons = await ButtonGenerationUtility.generateButtons(botResponse);
      } catch (buttonError) {
        console.error('Error generating buttons:', buttonError);
      }

      const newBotMessage = {
        ...botResponse,
        sender: "bot",
        buttons,
        id: Date.now(),
      };

      // Remove typing indicator and add bot message
      setMessages((prevMessages) =>
        prevMessages
          .filter((msg) => msg.id !== 'typing-indicator')
          .concat(newBotMessage)
      );
      setLastBotMessageId(newBotMessage.id);
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error("ChatbotPopup - Error handling message:", error);
      if (error.response?.status === 401) {
        sessionStorage.setItem('coach_pending', JSON.stringify({
          prompt: currentInput,
          returnTab: 'coach',
          timestamp: Date.now()
        }));
        setMessages((prevMessages) =>
          prevMessages
            .filter((msg) => msg.id !== 'typing-indicator')
            .concat({
              text: "Your session has expired. Redirecting to sign in...",
              sender: "bot",
              id: Date.now(),
            })
        );
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      } else {
        // Remove typing indicator and show error message
        setMessages((prevMessages) =>
          prevMessages
            .filter((msg) => msg.id !== 'typing-indicator')
            .concat({
              text: "Sorry, I couldn't process your request. Please try again later.",
              sender: "bot",
              id: Date.now(),
            })
        );
        triggerErrorPopup("An error occurred while processing your request. Please try again.");
      }
      if (onMessageSent) {
        onMessageSent();
      }
    } finally {
      setIsProcessing(false);
      setTimeout(() => {
        focusInput();
      }, 0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isProcessing) {
        handleSendMessage();
      }
      setTimeout(() => {
        focusInput();
      }, 0); // Refocus after handling the key press
    }
  };

  const renderMessageWithButtons = (message, buttons) => {
    console.log('ChatbotPopup - Rendering message with buttons:', message, buttons);

    return (
      <div className="message-section">
        <p>{message.message}</p>
        {message.sections &&
          message.sections.map((section, index) => (
            <div key={index} className="section-update">
              <h4>{section.name}</h4>
              <p>{section.fullContent}</p>
              <button
                className={`apply-button animated-button ${
                  loadingState[section.button.label] ? 'loading' : ''
                }`}
                onClick={async () => {
                  let sectionKey;
                  switch (section.button.action) {
                    case 'updateSummary': sectionKey = 'summary'; break;
                    case 'updateExperience': sectionKey = 'experience'; break;
                    case 'updateSkills': sectionKey = 'skills'; break;
                    case 'updateEducation': sectionKey = 'education'; break;
                    case 'updatePublications': sectionKey = 'publications'; break;
                    default:
                      console.error('Unknown action:', section.button.action);
                      return;
                  }

                  if (onProposeUpdate) {
                    onProposeUpdate({
                      sectionKey,
                      sectionName: section.name,
                      proposedContent: section.content
                    });
                  } else {
                    setLoadingState((prevState) => ({
                      ...prevState,
                      [section.button.label]: true,
                    }));

                    try {
                      let updatedData = { [sectionKey]: section.content };
                      if (onDataUpdate) {
                        onDataUpdate(updatedData);
                      }
                    } catch (error) {
                      console.error('Error applying section update:', error);
                      triggerErrorPopup('Failed to update section. Please try again.');
                    } finally {
                      setLoadingState((prevState) => ({
                        ...prevState,
                        [section.button.label]: false,
                      }));
                      setTimeout(() => {
                        focusInput();
                      }, 0); // Refocus after applying button action
                    }
                  }
                }}
              >
                {loadingState[section.button.label] ? (
                  <div className="spinner"></div>
                ) : (
                  section.button.label
                )}
              </button>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div ref={ref} className={`chatbot-popup ${isOpen ? 'open' : 'closed'}`}>
      <div className="chatbot-header">
        <span>Assistant</span>
        <button className="close-button" onClick={onClose} aria-label="Close Chatbot">
          ×
        </button>
      </div>
      <div className="chatbot-messages" ref={messagesContainerRef}>
        {messages.map((message, index) => (
          <div
            key={message.id || index}
            id={`message-${message.id}`}
            className={`chatbot-message ${message.sender} ${
              message.sender === 'bot' && message.id === lastBotMessageId
                ? 'animate-new'
                : ''
            }`}
          >
            {message.type === 'typing' ? (
              <div className="typing-indicator">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            ) : message.buttons ? (
              renderMessageWithButtons(message, message.buttons)
            ) : (
              message.text
            )}
          </div>
        ))}
      </div>
      <div className="chatbot-input-container">
        <input
          type="text"
          className="chatbot-input"
          value={input}
          ref={inputRef}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          disabled={isProcessing}
        />
        <button
          className="chatbot-send-button"
          onClick={handleSendMessage}
          disabled={isProcessing}
          aria-label="Send Message"
        >
          ➤
        </button>
      </div>
    </div>
  );
});

export default ChatbotPopup;
